import { theme } from "@src/theme";
import React from "react";
import { Controller } from "react-hook-form";
import styled, { css } from "styled-components";

export const AppInputWrapper = styled.div<{
  $leftIcon?: boolean;
  $iconRight?: boolean;
  $success?: boolean;
  disabled?: boolean;
  blueStyle?: boolean;
  overrideBorderBottomColor?: string;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  input {
    width: 100%;
    padding: 8px;
    padding-left: ${({ $leftIcon, blueStyle }): string =>
      $leftIcon ? "30px" : blueStyle ? "5px" : "0px"};
    padding-right: ${({ $iconRight, blueStyle }): string =>
      $iconRight ? "30px" : blueStyle ? "5px" : "0px"};
    border: ${({ blueStyle }): string =>
      blueStyle ? "1px solid " + theme.colors.blue : "none"};
    border-bottom: 1px solid
      ${({
        theme,
        $success,
        disabled,
        blueStyle,
        overrideBorderBottomColor,
      }): string =>
        overrideBorderBottomColor
          ? overrideBorderBottomColor
          : disabled
            ? theme.colors.gray
            : $success
              ? theme.colors.green
              : blueStyle
                ? theme.colors.blue
                : theme.colors.yellow};
    cursor: ${({ disabled }): string => (disabled ? "not-allowed" : "text")};
    background-color: ${({ theme, blueStyle }): string =>
      blueStyle ? theme.colors.darkBg : "transparent"};
    color: ${({ theme }): string => theme.colors.white};
    font-size: 1rem;

    border-radius: ${({ blueStyle }): string => (blueStyle ? "5px" : "0px")};
    color: ${({ theme, blueStyle }): string =>
      blueStyle ? theme.colors.yellow : theme.colors.white};
    &:focus {
      border-bottom: ${({ blueStyle }) => (blueStyle ? "1px" : "2px")} solid
        ${({
          theme,
          $success,
          blueStyle,
          overrideBorderBottomColor,
        }): string =>
          overrideBorderBottomColor
            ? overrideBorderBottomColor
            : $success
              ? theme.colors.green
              : blueStyle
                ? theme.colors.blue
                : theme.colors.yellow};
      outline: none;
    }

    &.error {
      border-bottom: 1px solid ${({ theme }): string => theme.colors.red};
    }
    &.success {
      border-bottom: ${({ blueStyle }) => (blueStyle ? "1px" : "2px")} solid
        ${({ theme, $success, overrideBorderBottomColor }): string =>
          overrideBorderBottomColor
            ? overrideBorderBottomColor
            : $success
              ? theme.colors.green
              : theme.colors.yellow};
    }

    &.range-input {
      border-bottom: none;
    }
  }
  .input-icon {
    position: absolute;
    font-size: 18px;
    color: ${({ theme }): string => theme.colors.yellow};
  }
  .input-icon-right {
    position: absolute;
    font-size: 18px;
    right: 0;
    color: ${({ theme }): string => theme.colors.yellow};
  }
  opacity: ${({ disabled }): string => (disabled ? "0.5" : "1")};
`;

export const InputLabel = styled.label<{ disabled?: boolean }>`
  opacity: ${({ disabled }): string => (disabled ? "0.5" : "1")};
  margin-top: 1rem;
  font-size: 1rem;
  color: ${({ theme }): string => theme.colors.yellow};
  font-weight: bold;
`;

export const ErrorMessage = styled.p`
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

export const ErrorLabel = styled.p`
  color: ${({ theme }): string => theme.colors.red};
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const trackH = "0.4em";
const thumbD = "10px";
const trackC = theme.colors.gray;
const filllC = theme.colors.yellow;

const track = css`
  box-sizing: border-box;
  border: none;
  height: 4px;
  background: ${trackC};
  border-radius: 8px;
`;

const trackFill = css`
  ${track};
  height: 6px;
  background-color: transparent;
  background-image:
    linear-gradient(${filllC}, ${filllC}), linear-gradient(${trackC}, ${trackC});
  background-size:
    var(--sx) 6px,
    calc(100% - var(--sx)) 4px;
  background-position:
    left center,
    right center;
  background-repeat: no-repeat;
`;

const fill = css`
  height: ${trackH};
  background: ${filllC};
  border-radius: 4px;
`;

const thumb = css`
  box-sizing: border-box;
  border: none;
  width: ${thumbD};
  height: ${thumbD};
  border-radius: 50%;
  background: ${theme.colors.yellow};
  box-shadow: 0px 0px 5px rgba(66, 97, 255, 0.5);
`;

const SliderInput = styled.input`
  &,
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
  }

  &:focus {
    outline: none;
  }

  &:focus::-webkit-slider-thumb {
    outline: ${theme.colors.opacity_yellow} auto 1px;
  }

  &:focus::-moz-range-thumb {
    outline: ${theme.colors.opacity_yellow} auto 1px;
  }

  &:focus::-ms-thumb {
    outline: ${theme.colors.opacity_yellow} auto 1px;
  }

  --range: calc(var(--max) - var(--min));
  --ratio: calc((var(--val) - var(--min)) / var(--range));
  --sx: calc(0.5 * ${thumbD} + var(--ratio) * (100% - ${thumbD}));

  margin: 0;
  padding: 0;
  height: ${thumbD};
  background: transparent;
  font:
    1em/1 arial,
    sans-serif;

  &::-webkit-slider-runnable-track {
    ${trackFill};
  }

  &::-moz-range-track {
    ${track};
  }

  &::-ms-track {
    ${track};
  }

  &::-moz-range-progress {
    ${fill};
  }

  &::-ms-fill-lower {
    ${fill};
  }

  &::-webkit-slider-thumb {
    margin-top: calc(0.5 * (${trackH} - ${thumbD}));
    ${thumb};
  }

  &::-moz-range-thumb {
    ${thumb};
  }

  &::-ms-thumb {
    margin-top: 0;
    ${thumb};
  }

  &::-ms-tooltip {
    display: none;
  }

  &::-moz-focus-outer {
    border: 0;
  }
`;
const RangeValueLabel = styled.div`
  display: flex;
  color: ${theme.colors.gray};
  padding: 0px 10px;
`;

interface AppInputProps {
  children?: React.ReactNode;
  label: string;
  name: string;
  control: any;
  errors: any;
  placeholder: string;
  type?: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  successStyle?: boolean;
  disabled?: boolean;
  displayInput?: boolean;
  min?: number;
  max?: number;
  blueStyle?: boolean;
}

const CustomAppInput = React.forwardRef<HTMLInputElement, AppInputProps>(
  (
    {
      label,
      name,
      control,
      errors,
      placeholder,
      type = "text",
      icon,
      iconRight,
      successStyle,
      disabled,
      children,
      displayInput = false,
      min = 0,
      max = 100,
      blueStyle = false,
    },
    ref
  ) => {
    return (
      <div style={{ marginBottom: "1rem", width: "100%", minHeight: 100 }}>
        <InputLabel disabled={disabled} htmlFor={name}>
          {label}
        </InputLabel>
        <AppInputWrapper
          blueStyle={blueStyle}
          disabled={disabled}
          $success={successStyle}
          $leftIcon={!!icon}
          $iconRight={!!iconRight}
        >
          {icon && <span className="input-icon">{icon}</span>}
          {iconRight && <span className="input-icon-right">{iconRight}</span>}
          <Controller
            name={name}
            control={control}
            render={({ field }) =>
              type === "range" ? (
                <>
                  <SliderInput
                    min={min}
                    max={max}
                    {...field}
                    ref={ref}
                    disabled={disabled}
                    id={name}
                    type={type}
                    placeholder={placeholder}
                    className={`app-input range-input ${errors[name] ? "error" : ""}`}
                    style={
                      {
                        width: "100%",
                        "--min": min,
                        "--max": max,
                        "--val": field.value,
                      } as React.CSSProperties
                    }
                  />
                  <RangeValueLabel>
                    <span>{field?.value || 0}</span>
                  </RangeValueLabel>
                </>
              ) : (
                <input
                  {...field}
                  ref={ref}
                  disabled={disabled}
                  id={name}
                  type={type}
                  placeholder={placeholder}
                  className={`app-input ${errors[name] ? "error" : ""}`}
                  style={{ display: displayInput ? "flex" : "none" }}
                />
              )
            }
          />
          {children}
        </AppInputWrapper>
        {errors[name] && (
          <ErrorLabel className="error-message">
            {errors[name]?.message}
          </ErrorLabel>
        )}
      </div>
    );
  }
);

CustomAppInput.displayName = "CustomAppInput"; // Set a display name for better debugging

export default CustomAppInput;
